import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Check, X, Users } from 'lucide-react';

const TeamApprovals = () => {
  const [team, setTeam] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [t, l, e] = await Promise.all([
        api.get('/me/team'),
        api.get('/me/team/leaves'),
        api.get('/me/team/expenses'),
      ]);
      setTeam(t.data);
      setLeaves(l.data);
      setExpenses(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const approveLeave = async (id) => {
    try { await api.put(`/leaves/${id}/approve`); toast.success('Approved'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };
  const rejectLeave = async (id) => {
    try { await api.put(`/leaves/${id}/reject?reason=Rejected`); toast.success('Rejected'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };
  const approveExp = async (id) => {
    try { await api.put(`/expenses/${id}/approve`); toast.success('Approved'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };
  const rejectExp = async (id) => {
    try { await api.put(`/expenses/${id}/reject`); toast.success('Rejected'); fetchAll(); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const pendingExpenses = expenses.filter(e => e.status === 'pending');

  return (
    <div className="space-y-6" data-testid="team-approvals-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Users />My Team</h1>
        <p className="text-muted-foreground">Approve team leaves and expenses</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Team Size</p><p className="text-2xl font-bold">{team.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending Leaves</p><p className="text-2xl font-bold text-amber-600">{pendingLeaves.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending Expenses</p><p className="text-2xl font-bold text-purple-600">{pendingExpenses.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved Today</p><p className="text-2xl font-bold text-emerald-600">{leaves.filter(l => l.status === 'approved').length + expenses.filter(e => e.status === 'approved').length}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="leaves">
        <TabsList>
          <TabsTrigger value="leaves" data-testid="tab-team-leaves">Leaves ({pendingLeaves.length})</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-team-expenses">Expenses ({pendingExpenses.length})</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team-list">Team ({team.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="leaves">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>From-To</TableHead><TableHead>Days</TableHead><TableHead>Reason</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {pendingLeaves.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No pending leaves</TableCell></TableRow>}
                {pendingLeaves.map(l => (
                  <TableRow key={l.id} data-testid={`team-leave-${l.id}`}>
                    <TableCell className="font-medium">{l.employee_name}</TableCell>
                    <TableCell><Badge variant="outline">{l.leave_type}</Badge></TableCell>
                    <TableCell>{l.start_date} → {l.end_date}</TableCell>
                    <TableCell>{l.days}</TableCell>
                    <TableCell className="max-w-xs truncate">{l.reason}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => approveLeave(l.id)} data-testid={`approve-leave-${l.id}`}><Check size={14} /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectLeave(l.id)} data-testid={`reject-leave-${l.id}`}><X size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="expenses">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Category</TableHead><TableHead>Amount</TableHead><TableHead>Description</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {pendingExpenses.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No pending expenses</TableCell></TableRow>}
                {pendingExpenses.map(e => (
                  <TableRow key={e.id} data-testid={`team-exp-${e.id}`}>
                    <TableCell className="font-medium">{e.employee_name}</TableCell>
                    <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                    <TableCell className="font-medium">{e.currency} {e.amount?.toLocaleString()}</TableCell>
                    <TableCell className="max-w-xs truncate">{e.description}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => approveExp(e.id)} data-testid={`approve-exp-${e.id}`}><Check size={14} /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectExp(e.id)} data-testid={`reject-exp-${e.id}`}><X size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="team">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Designation</TableHead><TableHead>Department</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {team.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No reportees assigned</TableCell></TableRow>}
                {team.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.employee_code}</TableCell>
                    <TableCell className="font-medium">{t.first_name} {t.last_name}</TableCell>
                    <TableCell>{t.designation}</TableCell>
                    <TableCell>{t.department}</TableCell>
                    <TableCell><Badge>{t.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamApprovals;
